import React, { useState } from 'react';
import { Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText, Toolbar, Divider } from '@mui/material';
import AppHeader from './AppHeader';

const DRAWER_WIDTH = 240;

/**
 * Shared shell for the Staff/Admin/Tenant dashboards: a fixed top AppHeader plus
 * a left-hand nav — permanent on desktop, an overlay drawer on small screens
 * (opened via the hamburger button AppHeader renders when given onMenuClick).
 *
 * `navItems`: [{ label, icon, Component }] — already filtered by the caller
 * (e.g. by permission) before being passed in.
 * Selection is controlled (`activeIndex` / `onSelect`) so callers keep owning
 * whatever state/routing they already had; this component only renders it.
 */
const DashboardLayout = ({ title, headerActions, navItems, activeIndex, onSelect }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const ActiveComponent = navItems[activeIndex]?.Component;

  const handleSelect = (index) => {
    onSelect(index);
    setMobileOpen(false);
  };

  const navList = (
    <List sx={{ px: 1, py: 1.5 }}>
      {navItems.map((item, index) => (
        <ListItemButton
          key={item.label}
          selected={activeIndex === index}
          onClick={() => handleSelect(index)}
          sx={{
            borderRadius: 1.5,
            mb: 0.5,
            '&.Mui-selected': {
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              '& .MuiListItemIcon-root': { color: 'inherit' },
              '&:hover': { bgcolor: 'primary.dark' },
            },
          }}
        >
          {item.icon && (
            <ListItemIcon sx={{ minWidth: 40, color: activeIndex === index ? 'inherit' : 'text.secondary' }}>
              {item.icon}
            </ListItemIcon>
          )}
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: activeIndex === index ? 600 : 500 }}
          />
        </ListItemButton>
      ))}
    </List>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppHeader title={title} onMenuClick={() => setMobileOpen(true)}>
        {headerActions}
      </AppHeader>

      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* Permanent sidebar — desktop */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            width: DRAWER_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              position: 'relative',
              borderRight: '1px solid',
              borderColor: 'divider',
            },
          }}
          open
        >
          {navList}
        </Drawer>

        {/* Overlay sidebar — mobile/tablet */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
          }}
        >
          <Toolbar />
          <Divider />
          {navList}
        </Drawer>

        <Box component="main" sx={{ flexGrow: 1, minWidth: 0, p: { xs: 2, sm: 3 } }}>
          {ActiveComponent && <ActiveComponent />}
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardLayout;
